import api from '../resources/axios';
import {
  Ticket,
  TicketMessage,
  TicketStatus,
  TicketPriority,
  TicketFilters,
} from '../resources/types/ticketTypes';

type Unsubscribe = () => void;

const LIST_POLL_MS = 15000;
const TICKET_POLL_MS = 5000;

function mapMessage(m: any): TicketMessage {
  return {
    id: m._id || m.id,
    sender: m.sender,
    senderId: m.senderId,
    text: m.text,
    attachments: m.attachments ?? [],
    createdAt: m.createdAt,
  };
}

function mapTicket(raw: any): Ticket {
  return {
    id: raw._id || raw.id,
    userId: raw.userId,
    category: raw.category,
    subject: raw.subject,
    description: raw.description,
    status: raw.status,
    priority: raw.priority,
    assignedTo: raw.assignedTo ?? null,
    messages: Array.isArray(raw.messages)
      ? raw.messages.map(mapMessage)
      : undefined,
    lastMessage: raw.lastMessage,
    messagesCount:
      raw.messagesCount ??
      (Array.isArray(raw.messages) ? raw.messages.length : 0),
    metadata: raw.metadata,
    createdAt: raw.createdAt || raw.updatedAt || new Date().toISOString(),
    updatedAt: raw.updatedAt || raw.createdAt || new Date().toISOString(),
  };
}

function buildListParams(filters: TicketFilters): Record<string, any> {
  const params: Record<string, any> = {};
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.category && filters.category !== 'all')
    params.category = filters.category;
  if (filters.priority && filters.priority !== 'all')
    params.priority = filters.priority;
  if (filters.assignedTo === 'me') params.assignedTo = 'me';
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  return params;
}

async function fetchTickets(filters: TicketFilters): Promise<Ticket[]> {
  const response = await api.get('/admin/tickets', {
    params: buildListParams(filters),
  });
  const list = response.data?.data?.tickets || [];
  return list.map(mapTicket);
}

async function fetchTicket(ticketId: string): Promise<Ticket | null> {
  const response = await api.get(`/admin/tickets/${ticketId}`);
  const raw = response.data?.data?.ticket;
  return raw ? mapTicket(raw) : null;
}

// Polls the ticket list on an interval (the REST API has no realtime stream).
export function subscribeToTickets(
  filters: TicketFilters,
  _adminId: string,
  callback: (tickets: Ticket[]) => void
): Unsubscribe {
  let cancelled = false;
  let delivered = false;
  let inFlight = false;

  const load = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      const tickets = await fetchTickets(filters);
      if (cancelled) return;
      delivered = true;
      callback(tickets);
    } catch (err) {
      console.error('Error loading tickets:', err);
      if (!cancelled && !delivered) {
        delivered = true;
        callback([]);
      }
    } finally {
      inFlight = false;
    }
  };

  load();
  const intervalId = setInterval(load, LIST_POLL_MS);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
}

// Polls a single ticket (with its messages) on an interval.
export function subscribeToTicket(
  ticketId: string,
  callback: (ticket: Ticket | null, messages: TicketMessage[]) => void
): Unsubscribe {
  let cancelled = false;
  let delivered = false;
  let inFlight = false;

  const load = async () => {
    if (inFlight) return;
    inFlight = true;
    try {
      const ticket = await fetchTicket(ticketId);
      if (cancelled) return;
      delivered = true;
      callback(ticket, ticket?.messages || []);
    } catch (err) {
      console.error('Error loading ticket:', err);
      if (!cancelled && !delivered) {
        delivered = true;
        callback(null, []);
      }
    } finally {
      inFlight = false;
    }
  };

  load();
  const intervalId = setInterval(load, TICKET_POLL_MS);

  return () => {
    cancelled = true;
    clearInterval(intervalId);
  };
}

export async function sendSupportMessage(
  ticketId: string,
  text: string,
  _senderId: string,
  attachments: string[] = []
): Promise<void> {
  await api.post(`/admin/tickets/${ticketId}/messages`, { text, attachments });
}

export async function assignTicket(
  ticketId: string,
  adminId: string
): Promise<void> {
  await api.patch(`/admin/tickets/${ticketId}`, {
    assignedTo: adminId,
    status: 'in_progress',
  });
}

export async function unassignTicket(ticketId: string): Promise<void> {
  await api.patch(`/admin/tickets/${ticketId}`, {
    assignedTo: null,
    status: 'open',
  });
}

export async function updateTicketStatus(
  ticketId: string,
  status: TicketStatus
): Promise<void> {
  await api.patch(`/admin/tickets/${ticketId}`, { status });
}

export async function updateTicketPriority(
  ticketId: string,
  priority: TicketPriority
): Promise<void> {
  await api.patch(`/admin/tickets/${ticketId}`, { priority });
}

// No dedicated counts endpoint: derive each status total from pagination.count.
export async function getTicketCounts(): Promise<Record<TicketStatus, number>> {
  const statuses: TicketStatus[] = [
    'open',
    'in_progress',
    'resolved',
    'closed',
  ];
  const results = await Promise.all(
    statuses.map(async status => {
      try {
        const response = await api.get('/admin/tickets', {
          params: { status, limit: 1, page: 1 },
        });
        return { status, count: response.data?.pagination?.count ?? 0 };
      } catch {
        return { status, count: 0 };
      }
    })
  );
  return Object.fromEntries(
    results.map(({ status, count }) => [status, count])
  ) as Record<TicketStatus, number>;
}

export function formatDuration(ms: number): string {
  if (ms < 60000) return '< 1 хв';
  if (ms < 3600000) return `${Math.round(ms / 60000)} хв`;
  if (ms < 86400000) return `${Math.round(ms / 3600000)} год`;
  return `${Math.round(ms / 86400000)} дн`;
}
